/**
 * readArCellOperand
 *
 * The single raw-read rule for formula operands, shared by evaluation
 * (computeArRowFormulas) and the provenance fingerprint
 * (computeArFormulaFingerprint), so the stamp always hashes exactly what
 * evaluation consumes. Dotted paths read compound sub-values
 * (`cells[root].value.fields[sub].value`); plain paths read `value`;
 * anything absent resolves to 0, matching the engine's missing-operand
 * semantics.
 * `calcInput` is deliberately NOT consulted. It is the engine's own output
 * cache, and evaluateRowFormulas never routes an engine-owned typeId through
 * here, so the only cell this could ever read is an ORPHAN — a target dropped
 * from `formulaSequence`, whose frozen cache would otherwise shadow the value
 * the user types, invisibly and forever (the fingerprint hashes the same
 * orphan, so the stamp reads current and no alarm fires).
 */

import type { ArCell, ArCellValue, TypeId } from '../System.Artifacts.types';

function readArCellOperand(
  cells: Partial<Record<TypeId, ArCell>> | undefined,
  path: string,
): ArCellValue {
  const dotIndex = path.indexOf('.');
  if (dotIndex !== -1) {
    const root = path.slice(0, dotIndex) as TypeId;
    const subFieldId = path.slice(dotIndex + 1);
    const compositeValue = cells?.[root]?.value as
      | { fields?: Record<string, { value?: ArCellValue }> }
      | undefined;
    return compositeValue?.fields?.[subFieldId]?.value ?? 0;
  }
  return cells?.[path as TypeId]?.value ?? 0;
}

export { readArCellOperand };
